import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	fetchComplianceEvidence,
	fetchStudentInsights,
	updateComplianceEvidence,
} from 'api/reports'
import { ROUTES } from 'shared/config/routes'
import { JURISDICTION_REPORTS } from '../../data/reportFixtures'
import { useReportsPeriodControls } from '../../hooks/useReportsPeriodControls'
import { buildInsightsDateParams } from '../../utils/reportsPeriods'
import { downloadCsv } from '../../utils/reportExport'

const percent = (value, total) => (total ? Math.round((value / total) * 100) : 0)

const cohortToGrade = cohort => {
	if (cohort === 'eligible') return 'all'
	const match = String(cohort || '').match(/^grade-(\d+)$/)
	return match ? match[1] : 'all'
}

/**
 * Government Compliance checklist + student KPI snapshot from API.
 * Reporting period from shared MobX applied state.
 */
export const useGovernmentReport = () => {
	const { jurisdictionId } = useParams()
	const navigate = useNavigate()
	const periodControls = useReportsPeriodControls()
	const [cohort, setCohort] = useState('eligible')
	const [insightsStatus, setInsightsStatus] = useState('loading')
	const [insightsError, setInsightsError] = useState(null)
	const [insights, setInsights] = useState(null)
	const [evidenceStatus, setEvidenceStatus] = useState('loading')
	const [evidenceError, setEvidenceError] = useState(null)
	const [evidenceCollected, setEvidenceCollected] = useState({})
	const [savingEvidenceId, setSavingEvidenceId] = useState(null)

	const profile = JURISDICTION_REPORTS[jurisdictionId] || JURISDICTION_REPORTS['uae-dubai']
	const jurisdictionKey = profile.id
	const gradeKey = cohortToGrade(cohort)
	const { appliedPeriod, appliedCustomFrom, appliedCustomTo, periodLabel } = periodControls

	useEffect(() => {
		let active = true
		const dateParams = buildInsightsDateParams({
			period: appliedPeriod,
			customFrom: appliedCustomFrom,
			customTo: appliedCustomTo,
		})

		if (!dateParams) {
			setInsightsStatus('idle')
			setInsightsError(null)
			return () => {
				active = false
			}
		}

		setInsightsStatus('loading')
		setInsightsError(null)

		const load = async () => {
			try {
				const data = await fetchStudentInsights({ ...dateParams, grade: gradeKey })
				if (!active) return
				setInsights(data)
				setInsightsStatus('success')
				setInsightsError(null)
			} catch (err) {
				if (!active) return
				setInsights(null)
				setInsightsStatus('error')
				setInsightsError(
					err?.response?.data?.error ||
						err?.message ||
						'Failed to load student insights',
				)
			}
		}

		load()
		return () => {
			active = false
		}
	}, [appliedPeriod, appliedCustomFrom, appliedCustomTo, gradeKey])

	useEffect(() => {
		let active = true
		setEvidenceStatus('loading')
		setEvidenceError(null)

		const load = async () => {
			try {
				const payload = await fetchComplianceEvidence({ jurisdiction: jurisdictionKey })
				if (!active) return
				setEvidenceCollected(payload?.evidenceCollected || {})
				setEvidenceStatus('success')
				setEvidenceError(null)
			} catch (err) {
				if (!active) return
				setEvidenceCollected({})
				setEvidenceStatus('error')
				setEvidenceError(
					err?.response?.data?.error ||
						err?.message ||
						'Failed to load compliance evidence',
				)
			}
		}

		load()
		return () => {
			active = false
		}
	}, [jurisdictionKey])

	const eligibleTotal = Number(insights?.complianceLive?.eligibleTotal || insights?.roster?.total || 0)
	const assessed = Number(insights?.complianceLive?.assessed || 0)
	const destinations = Number(insights?.complianceLive?.withDestinationPlan || 0)
	const outcomes = Number(insights?.complianceLive?.withFinalOutcome || 0)

	const readyCount = useMemo(
		() => profile.requirements.filter(row => Boolean(evidenceCollected[row.id])).length,
		[profile.requirements, evidenceCollected],
	)
	const readiness = percent(readyCount, profile.requirements.length)

	const handleToggleEvidence = useCallback(
		async (evidenceId, nextCollected) => {
			const previous = evidenceCollected
			setEvidenceCollected(current => ({ ...current, [evidenceId]: nextCollected }))
			setSavingEvidenceId(evidenceId)
			setEvidenceError(null)

			try {
				const payload = await updateComplianceEvidence({
					jurisdictionId: jurisdictionKey,
					evidenceId,
					collected: nextCollected,
				})
				setEvidenceCollected(payload?.evidenceCollected || {})
			} catch (err) {
				setEvidenceCollected(previous)
				setEvidenceError(
					err?.response?.data?.error ||
						err?.message ||
						'Failed to save evidence state',
				)
			} finally {
				setSavingEvidenceId(null)
			}
		},
		[evidenceCollected, jurisdictionKey],
	)

	const reportRows = profile.requirements.map(row => ({
		jurisdiction: profile.region,
		regulator: profile.regulator,
		reporting_period: periodLabel,
		cohort,
		evidence_id: row.id,
		evidence_type: row.requirement,
		owner: row.owner,
		collected: Boolean(evidenceCollected[row.id]),
		readiness_collected: readyCount,
		readiness_total: profile.requirements.length,
		readiness_pct: readiness,
	}))

	const handleExport = () => downloadCsv(`${profile.id}-evidence-summary.csv`, reportRows)
	const handleCountryChange = nextJurisdictionId =>
		navigate(`${ROUTES.REPORTS}/compliance/${nextJurisdictionId}`)

	return {
		...periodControls,
		period: periodControls.selectedPeriod,
		customFrom: periodControls.draftFrom,
		customTo: periodControls.draftTo,
		setPeriod: periodControls.onPeriodChange,
		setCustomFrom: periodControls.onCustomFromChange,
		setCustomTo: periodControls.onCustomToChange,
		profile,
		eligibleTotal,
		assessed,
		destinations,
		outcomes,
		insightsStatus,
		insightsError,
		readyCount,
		readiness,
		evidenceCollected,
		evidenceStatus,
		evidenceError,
		savingEvidenceId,
		handleToggleEvidence,
		cohort,
		setCohort,
		handleExport,
		handleCountryChange,
		jurisdictionOptions: Object.values(JURISDICTION_REPORTS),
	}
}

export { JURISDICTION_REPORTS }
